import React, { useEffect, useState } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { keyframes } from '@emotion/react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import logoImg from '../assets/images/talktokam.webp';

const wave = keyframes`
  0% { transform: translateX(0) translateY(0); }
  25% { transform: translateX(-15%) translateY(5px); }
  50% { transform: translateX(-30%) translateY(0); }
  75% { transform: translateX(-15%) translateY(-3px); }
  100% { transform: translateX(0) translateY(0); }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0) scale(1); opacity: 0.3; }
  50% { transform: translateY(-20px) scale(1.1); opacity: 0.5; }
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

const fadeIn = keyframes`
  0% { opacity: 0; transform: translateY(20px); }
  100% { opacity: 1; transform: translateY(0); }
`;

const AUTH_API = process.env.REACT_APP_AUTH_API || "http://localhost:5000/api/auth";

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const [status, setStatus] = useState('verifying');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('Invalid verification link. No token found.');
      return;
    }

    const verify = async () => {
      try {
        const res = await fetch(`${AUTH_API}/verify-email-token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.message);
        setStatus('success');
        setMessage('Your email has been verified successfully!');
      } catch (err) {
        const msg = err.message || '';
        if (msg.includes('expired')) {
          setStatus('error');
          setMessage('This verification link has expired. Please request a new one.');
        } else {
          setStatus('error');
          setMessage(msg || 'Verification failed. The link may be expired or invalid.');
        }
      }
    };

    verify();
  }, [token]);

  return (
    <Box sx={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      position: 'relative', overflow: 'hidden',
      background: 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 30%, #ccfbf1 70%, #f0fdf4 100%)',
    }}>
      <Box sx={{
        position: 'absolute', bottom: 0, left: 0, right: 0, height: '40%',
        background: 'linear-gradient(180deg, transparent, rgba(99,102,241,0.08) 40%, rgba(99,102,241,0.12))',
        animation: `${wave} 6s ease-in-out infinite`,
        pointerEvents: 'none',
      }} />
      <Box sx={{
        position: 'absolute', bottom: 0, left: 0, right: 0, height: '30%',
        background: 'linear-gradient(180deg, transparent, rgba(16,185,129,0.06) 50%, rgba(16,185,129,0.1))',
        animation: `${wave} 8s ease-in-out infinite`,
        animationDelay: '-2s',
        pointerEvents: 'none',
      }} />

      {[...Array(6)].map((_, i) => (
        <Box key={i} sx={{
          position: 'absolute',
          width: 20 + Math.random() * 40,
          height: 20 + Math.random() * 40,
          borderRadius: '50%',
          background: 'radial-gradient(circle at 30% 30%, rgba(99,102,241,0.15), rgba(99,102,241,0.05))',
          top: `${10 + Math.random() * 80}%`,
          left: `${10 + Math.random() * 80}%`,
          animation: `${float} ${3 + Math.random() * 4}s ease-in-out infinite`,
          animationDelay: `${Math.random() * 3}s`,
          pointerEvents: 'none',
        }} />
      ))}

      <Box sx={{
        position: 'relative', zIndex: 1,
        animation: `${fadeIn} 0.6s ease-out`,
        textAlign: 'center',
        maxWidth: 420, width: '90%',
      }}>
        <Box sx={{
          width: 80, height: 80, borderRadius: '50%', overflow: 'hidden',
          mx: 'auto', mb: 3,
          animation: status === 'verifying' ? `${pulse} 2s ease-in-out infinite` : 'none',
          boxShadow: '0 0 40px rgba(99,102,241,0.25)',
          border: '3px solid rgba(255,255,255,0.9)',
        }}>
          <Box component="img" src={logoImg} alt="talktokam"
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        </Box>

        <Box sx={{
          bgcolor: 'rgba(255,255,255,0.75)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderRadius: 4,
          p: 4,
          border: '1px solid rgba(255,255,255,0.5)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.06)',
        }}>
          {status === 'verifying' && (
            <>
              <Box sx={{
                width: 48, height: 48, borderRadius: '50%', mx: 'auto', mb: 2,
                border: '3px solid #6366f1',
                borderTopColor: 'transparent',
                animation: `${spin} 0.8s linear infinite`,
              }} />
              <Typography sx={{ fontWeight: 700, fontSize: 18, color: '#1f2937', mb: 0.5 }}>
                Verifying your email…
              </Typography>
              <Typography sx={{ fontSize: 13, color: '#6b7280' }}>
                Please wait while we confirm your email address.
              </Typography>
            </>
          )}

          {status === 'success' && (
            <>
              <Box sx={{
                width: 56, height: 56, borderRadius: '50%', mx: 'auto', mb: 2,
                background: 'linear-gradient(135deg, #10b981, #34d399)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 28, color: '#fff',
              }}>
                ✓
              </Box>
              <Typography sx={{ fontWeight: 700, fontSize: 18, color: '#1f2937', mb: 0.5 }}>
                Email Verified!
              </Typography>
              <Typography sx={{ fontSize: 13, color: '#6b7280', mb: 3 }}>
                {message}
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate('/login')}
                sx={{
                  py: 1.2, px: 4, borderRadius: 2, textTransform: 'none', fontWeight: 700,
                  background: 'linear-gradient(135deg, #4F46E5, #7C3AED)',
                  boxShadow: '0 4px 16px rgba(79,70,229,0.3)',
                  '&:hover': { boxShadow: '0 8px 24px rgba(79,70,229,0.4)' },
                }}
              >
                Go to Login
              </Button>
            </>
          )}

          {status === 'error' && (
            <>
              <Box sx={{
                width: 56, height: 56, borderRadius: '50%', mx: 'auto', mb: 2,
                background: 'linear-gradient(135deg, #ef4444, #f87171)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 28, color: '#fff',
              }}>
                ✕
              </Box>
              <Typography sx={{ fontWeight: 700, fontSize: 18, color: '#1f2937', mb: 0.5 }}>
                Verification Failed
              </Typography>
              <Typography sx={{ fontSize: 13, color: '#6b7280', mb: 3 }}>
                {message}
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate('/login')}
                sx={{
                  py: 1.2, px: 4, borderRadius: 2, textTransform: 'none', fontWeight: 700,
                  background: 'linear-gradient(135deg, #4F46E5, #7C3AED)',
                  boxShadow: '0 4px 16px rgba(79,70,229,0.3)',
                  '&:hover': { boxShadow: '0 8px 24px rgba(79,70,229,0.4)' },
                }}
              >
                Go to Login
              </Button>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default VerifyEmail;
